import type { EvalCheckResult, EvalRunData, EvalSnapshot, EvalVerdict } from "@global/types/ai-evals";

/** A check result with the run it belongs to. */
export type Row = EvalCheckResult & { scenarioId: string; run: number };

export type RunKey = { scenarioId: string; run: number };

export type Scope = "interview" | "case";

const POINTS: Record<Exclude<EvalVerdict, "na">, number> = { pass: 1, concerns: 0.5, fail: 0 };

export const VERDICTS: EvalVerdict[] = ["pass", "concerns", "fail", "na"];

export function rowsOf(data: EvalRunData | null): Row[] {
  if (!data) return [];
  // Runs graded before 2026-10-16 can hold a judge result without a verdict (one valid vote); it carries no grade.
  return data.results.runs.flatMap((r) =>
    r.results.filter((result) => VERDICTS.includes(result.verdict)).map((result) => ({ ...result, scenarioId: r.scenarioId, run: r.run })),
  );
}

/** Mean points over the applicable results (pass 1, concerns ½, fail 0); null when nothing applied. */
export function score(rows: Row[]): number | null {
  const scored = rows.filter((r) => r.verdict !== "na");
  if (scored.length === 0) return null;
  return scored.reduce((sum, r) => sum + POINTS[r.verdict as Exclude<EvalVerdict, "na">], 0) / scored.length;
}

export type Counts = Record<EvalVerdict, number> & { applicable: number; total: number };

/** How many results got each verdict; `applicable` excludes N/A and is the base the score averages over. */
export function counts(rows: Row[]): Counts {
  const c = { pass: 0, concerns: 0, fail: 0, na: 0 };
  for (const r of rows) c[r.verdict]++;
  return { ...c, applicable: c.pass + c.concerns + c.fail, total: rows.length };
}

/** "3 (25 %)": the share of pass, concerns and fail is of the applicable results, the share of N/A of all. */
export function countLabel(c: Counts, verdict: EvalVerdict): string {
  const base = verdict === "na" ? c.total : c.applicable;
  return base ? `${c[verdict]} (${Math.round((c[verdict] / base) * 100)} %)` : "0";
}

export const scopeOf = (group: string): Scope => (group.startsWith("I") ? "interview" : "case");

export const pct = (value: number | null) => (value === null ? "–" : String(Math.round(value * 100)));

/** Groups in catalog order, as [id, title]. */
export function groupsOf(data: EvalRunData): [string, string][] {
  return [...new Map(data.checks.map((c) => [c.group, c.groupTitle])).entries()];
}

export const questionsOf = (snapshot: EvalSnapshot) =>
  snapshot.transcript.filter((turn) => turn.role === "assistant" && !turn.done).length;

export function snapshotOf(data: EvalRunData | null, key: RunKey): EvalSnapshot | null {
  return data?.snapshots.find((s) => s.scenarioId === key.scenarioId && s.run === key.run) ?? null;
}

export const forRun = (rows: Row[], key: RunKey) => rows.filter((r) => r.scenarioId === key.scenarioId && r.run === key.run);

export const costOf = (data: EvalRunData) => Object.values(data.results.usage).reduce((sum, u) => sum + u.usd, 0);
